// A caster as a pawn whose head is the sign of the caster, in the box of a
// piece glyph. The body is the body of a pawn. The head is a shape for each
// caster. A cut is a hole in the pawn of the caster select and the primary
// color on a piece of the board. A second part is the secondary color.
// Garrett, 2026-10-15, when the casters moved from the bishops to the pawns:
// "make really elegant and beautiful pawn caster designs". He chose this from
// a page: "I like B, sign head". The other option kept the round head of a
// pawn and fitted the motif to it. Until then a caster was a bishop that wore
// its motif, in `casterBishopShapes.ts`. The motifs come from the square
// symbols in `casterMarkShapes.ts`.

import type { CasterId } from '../content/casters';

/** The parts of a motif. */
export type CasterMotif = {
  /** Holes in the shape. Two shapes in one path make a ring. */
  cuts: readonly string[];
  /** Parts in the secondary color, over the shape and over its cuts. */
  second: readonly string[];
};

type CasterPawnShape = CasterMotif & { head: string };

const rect = (x: number, y: number, width: number, height: number): string =>
  `M${x} ${y}h${width}v${height}h${-width}z`;

const polygon = (...points: readonly (readonly [number, number])[]): string =>
  `M${points.map(([x, y]) => `${x} ${y}`).join('L')}z`;

const disc = (centerX: number, centerY: number, radius: number): string =>
  `M${centerX - radius} ${centerY}a${radius} ${radius} 0 1 0 ${radius * 2} 0a${radius} ${radius} 0 1 0 ${-radius * 2} 0z`;

/** The body of a pawn, up to its neck. A head joins it at the neck. */
const PAWN_BODY = 'M30 86 L70 86 L70 78 L62 72 L58 47 L42 47 L38 72 L30 78 Z';

/** The head of a pawn with no sign: a disc on the neck. */
const PLAIN_HEAD = disc(50, 36.5, 14);

/** The chevron on the foot of Marathoné. */
const FOOT_CHEVRON = polygon([30, 81], [50, 65], [70, 81], [70, 89], [50, 73], [30, 89]);

/** A shape may go past the outline of the pawn. The drawing clips it. */
const SHAPES: Record<CasterId, CasterPawnShape> = {
  // A page with a folded corner and two lines of writing.
  parch: {
    head: polygon([36, 20], [56, 20], [64, 28], [64, 49], [36, 49]),
    cuts: [`${rect(40, 30, 14, 3.5)}${rect(40, 37, 20, 3.5)}`],
    second: [polygon([56, 20], [56, 28], [64, 28])],
  },
  // A head that points up, with a chevron in it, and a chevron on the foot.
  marathone: {
    head: polygon([35, 44], [50, 20], [65, 44], [58, 49], [42, 49]),
    cuts: [polygon([42, 41], [50, 29], [58, 41], [58, 46], [50, 35], [42, 46])],
    second: [FOOT_CHEVRON],
  },
  // A square head with a square ring and a core.
  'claude-opus': {
    head: rect(36, 20, 28, 29),
    cuts: [`${rect(39.5, 23.5, 21, 21)}${rect(44, 28, 12, 12)}`],
    second: [rect(47, 31, 6, 6)],
  },
  // The round head in the second color, and a band across the body.
  sinistri: {
    head: disc(50, 34, 15),
    cuts: [polygon([72, 50], [72, 61], [28, 87], [28, 76])],
    second: [disc(50, 34, 15.5)],
  },
  // A head like a blade that points down, with a guard over it.
  stabb: {
    head: polygon([36, 21], [64, 21], [64, 31], [50, 49], [36, 31]),
    cuts: [polygon([43, 31], [57, 31], [50, 41])],
    second: [rect(36, 21, 28, 6)],
  },
  // Two halves of a head, one lower than the other, with stitches across.
  frenkelstone: {
    head: 'M49 19A13 13 0 0 0 49 45z M51 23A13 13 0 0 1 51 49z',
    cuts: [`${rect(43, 27, 14, 3)}${rect(43, 35, 14, 3)}`],
    second: ['M51 23A13 13 0 0 1 51 49z'],
  },
  // Three steps. The top step in the second color, the middle in the first.
  upmost: {
    head: `${rect(45, 20, 10, 8)}${rect(41, 28, 18, 9)}${rect(37, 37, 26, 11)}`,
    cuts: [rect(41, 28, 18, 9)],
    second: [rect(45, 20, 10, 8)],
  },
  // A head with two round corners and two square ones.
  stokaz: {
    head: 'M36 34A14 14 0 0 1 50 20L64 20L64 34A14 14 0 0 1 50 48L36 48z',
    cuts: ['M50 34L50 23A11 11 0 0 0 39 34z', 'M50 34L50 45A11 11 0 0 0 61 34z'],
    second: [rect(54, 24, 7, 7), rect(39, 37, 7, 7)],
  },
};

/** A caster with no drawing yet gets a plain pawn. */
const PLAIN_PAWN: CasterPawnShape = { head: PLAIN_HEAD, cuts: [], second: [] };

const shapeOf = (id: CasterId): CasterPawnShape => SHAPES[id] ?? PLAIN_PAWN;

/** The outline of the pawn of a caster: the body and the head of its sign. */
export const casterPawnOutline = (id: CasterId): string => `${PAWN_BODY} ${shapeOf(id).head}`;

export const casterPawnMotif = (id: CasterId): CasterMotif => shapeOf(id);

/** The highest point of any head, in glyph units. A mark that points at the pawn stops over it. */
export const CASTER_PAWN_TOP = 19;
